import React, { FC, ForwardedRef, forwardRef, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import requiredValidator from '../../../validators/requiredValidator';
import Control from '../Control';
import { ToggleButtonListControlComposedProps, ToggleButtonListDefaultProps } from './ToggleButtonListControl';

const withControl = <Value, StateValue>(WrappedComponent: FC<ToggleButtonListControlComposedProps<Value, StateValue>>) => {
  const WithControl = forwardRef(({ name, required, recommended, textOnly, readOnly, ...props }: ToggleButtonListControlComposedProps<Value, StateValue>, ref: ForwardedRef<HTMLInputElement>) => {
    const defaultValidators = useMemo(() => ({
      required: requiredValidator,
      recommended: { validator: requiredValidator, type: 'warning' },
    }), []);

    return (
      <Control<Value, StateValue, HTMLInputElement, { required?: boolean; recommended?: boolean }>
        {...props}
        required={required}
        recommended={recommended}
        name={name}
        defaultValidators={defaultValidators}
        readOnly={readOnly}
        textOnly={textOnly}
      >
        {({
          id, value, isValid, hasWarning, hasCaution, hasInfo, isTouched, errorMessagesId, onValueChange: onValueChangeCallback,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            name={name}
            value={value}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            hasInfo={hasInfo}
            isTouched={isTouched}
            errorMessagesId={errorMessagesId}
            onValueChangeCallback={onValueChangeCallback}
            readOnly={readOnly}
            textOnly={textOnly}
            required={required}
            ref={ref}
          />
        )}
      </Control>
    );
  }) as FC<ToggleButtonListControlComposedProps<Value, StateValue>>;

  WithControl.defaultProps = ToggleButtonListDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withControl;
